import type { Hypothesis } from '../api/useHypotheses.ts';
import { retningOrd, tall } from '../hypoteser/setning.ts';

/**
 * Forhåndsutfyllingen av programskjemaet fra hypotesen (H3.4, `kjerne.md` 8.2, kontraktens regel på
 * `createProgram`). En KLIENTREGEL: ingenting finnes på tjeneren før eieren lagrer, og hvert felt er
 * et forslag hun leser og kan endre.
 *
 *   name ← title                     (testerne ser den, P1)
 *   hypothesis ← FØRSTE setning       «Vi tror at {change} for {audience} gir {effect}.» (P2)
 *   successCriterion.text ← metricName
 *   successCriterion.target ← retning, terskel og enhet («minst 46 %»)
 *   opensAt/closesAt ← testperioden
 *
 * A6: over 280 tegn fylles hypotesen IKKE ut og kortes aldri — feltet står tomt, og hele setningen
 * vises over det som kilden. Produktet, flagget, vilkårene, kontakttaket og oppgavene fylles aldri
 * ut: hypotesen har ikke noe produkt.
 */

/** Kontraktens tak: ProgramCreate.name, .hypothesis, SuccessCriterion.text og .target. */
const TAK = { navn: 80, hypotese: 280, kriterium: 280, maltall: 80 } as const;

export type UtfyltFelt = 'navn' | 'hypotese' | 'kriterium' | 'maltall' | 'fra' | 'til';

export interface Utfylling {
  /** Verdiene som ble fylt ut. Et felt som ikke kunne fylles, mangler. */
  verdier: Partial<Record<UtfyltFelt, string>>;
  /** Første setning når den er for lang til feltet (A6) — vist over det tomme feltet, aldri kortet. */
  helSetning?: string;
}

/** «Vi tror at {change} for {audience} gir {effect}.» — de samme ordene som tjenerens setning. */
export function foersteSetning(h: Pick<Hypothesis, 'change' | 'audience' | 'effect'>): string | null {
  const [endring, hvem, effekt] = [h.change?.trim(), h.audience?.trim(), h.effect?.trim()];
  if (!endring || !hvem || !effekt) return null;
  return `Vi tror at ${endring} for ${hvem} gir ${effekt}.`;
}

/** «minst 46 %» — retningen, terskelen og enheten som i setningen. */
export function maltallFra(h: Pick<Hypothesis, 'direction' | 'threshold' | 'unit'>): string | null {
  if (h.threshold == null || !h.direction) return null;
  return `${retningOrd(h.direction)} ${tall(h.threshold)}${h.unit?.trim() ? ` ${h.unit.trim()}` : ''}`;
}

export function utfyllingFra(h: Hypothesis): Utfylling {
  const verdier: Utfylling['verdier'] = {};
  const navn = h.title?.trim();
  if (navn && navn.length <= TAK.navn) verdier.navn = navn;
  const setning = foersteSetning(h);
  let helSetning: string | undefined;
  if (setning && setning.length <= TAK.hypotese) verdier.hypotese = setning;
  else if (setning) helSetning = setning;
  const kriterium = h.metricName?.trim();
  if (kriterium && kriterium.length <= TAK.kriterium) verdier.kriterium = kriterium;
  const maltall = maltallFra(h);
  if (maltall && maltall.length <= TAK.maltall) verdier.maltall = maltall;
  if (h.testStartsOn) verdier.fra = h.testStartsOn;
  if (h.testEndsOn) verdier.til = h.testEndsOn;
  return { verdier, ...(helSetning ? { helSetning } : {}) };
}
